import { BadgeCheck, Check, NotebookPen } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { PilotChecklistItem, PilotFact, PilotState } from "../../../lib/callPilot";
import { FactList } from "../context/FactList";
import { GuideList } from "./GuideList";

/** Same ceiling the server applies to the model's own shortlist - the reader is on the phone. */
const MAX_FALLBACK_QUESTIONS = 3;

/**
 * The half of the panel that is the point of the feature: what has not been
 * asked yet, and what we already know about this company.
 *
 * Every fact wears where it came from. That is not decoration - it is the line
 * between "our scanner saw this on their site" and "a model said it", and the
 * operator is about to repeat it out loud on a phone call. The server only ever
 * sends facts it can back with a stored row, and the badge is how the reader
 * can tell without knowing that.
 */
export function SuggestionList({
  state,
  checklist,
  facts,
  hadContact,
  scanUncertain,
  onAddToNote,
}: {
  state: PilotState;
  checklist: PilotChecklistItem[];
  facts: PilotFact[];
  hadContact: boolean;
  scanUncertain: boolean;
  onAddToNote: (text: string) => void;
}) {
  const t = useT();

  // `suggestedAtSeq` is -1 until the model has completed a suggestion pass, and
  // that is the whole difference between "everything has been asked" and "we
  // have not heard back yet". Reading only `open.length` conflated the two, so a
  // call whose first window failed was told all six checklist points were
  // covered while the operator had asked none of them.
  const advised = state.suggestedAtSeq >= 0;
  // Until then the stored checklist is the honest answer: these are the
  // questions, heaviest first, minus anything already marked covered.
  const fallback = advised
    ? []
    : [...checklist]
        .filter((item) => !state.answered.includes(item.id))
        .sort((a, b) => b.weight - a.weight)
        .slice(0, MAX_FALLBACK_QUESTIONS)
        .map((item) => ({ id: item.id, question: item.question, why: item.why }));
  const questions = advised ? state.open : fallback;

  return (
    // No `shrink-0` here, however much this column wants its full height: a
    // utility beats the component layer, so it would override the `flex-shrink`
    // in `.crm-cp-side` and push the suggestions straight out of the modal.
    <div className="crm-cp-side min-h-0 overflow-y-auto px-4 py-3.5">
      <section>
        <h4 className="crm-cp-h">{t("calls.pilot.askNext")}</h4>
        {questions.length === 0 ? (
          <p className="mt-1.5 text-[12px] leading-[1.55] text-muted-foreground">
            {t(advised ? "calls.pilot.nothingOpen" : "calls.pilot.noChecklist")}
          </p>
        ) : (
          <ul className="mt-2 flex flex-col gap-2">
            {questions.map((q) => (
              <li key={q.id} className="crm-cp-ask">
                <p className="text-[12.75px] font-[650] leading-[1.45]">{q.question}</p>
                {q.why ? <p className="crm-cp-why">{q.why}</p> : null}
                <button
                  type="button"
                  className="crm-cp-add"
                  onClick={() => onAddToNote(q.question)}
                  title={t("calls.pilot.addToNote")}
                >
                  <NotebookPen className="size-3" />
                  {t("calls.pilot.addToNote")}
                </button>
              </li>
            ))}
          </ul>
        )}
        {!advised && questions.length > 0 ? (
          // Said out loud, because the operator is about to read these on a call:
          // this is the stored checklist in weight order, not the model's reading
          // of the conversation so far.
          <p className="crm-cp-why mt-1.5">{t("calls.pilot.fromChecklist")}</p>
        ) : null}
        {state.answered.length > 0 ? (
          <p className="crm-cp-done">
            <Check className="size-3" />
            {/* The variable is `n` because that is the one the catalogue
                pluralises on: English breaks at exactly 1, Estonian does not. */}
            {t("calls.pilot.answeredCount", { n: state.answered.length })}
          </p>
        ) : null}
      </section>

      {/* Directly under the questions, and above the facts, because this is the
          only card that tells the operator what to DO rather than what to say.
          A guide found after the call is a guide that was not followed. */}
      <GuideList guides={state.guides} onAddToNote={onAddToNote} />

      <section className="mt-4">
        <h4 className="crm-cp-h">{t("calls.pilot.known")}</h4>
        {facts.length === 0 ? (
          <p className="mt-1.5 text-[12px] leading-[1.55] text-muted-foreground">
            {t(scanUncertain ? "calls.pilot.scanUncertain" : "calls.pilot.noFacts")}
          </p>
        ) : (
          <FactList facts={facts} highlights={state.highlights} onAddToNote={onAddToNote} />
        )}
        <p className="crm-cp-track">
          <BadgeCheck className="size-3" />
          {t(hadContact ? "calls.pilot.trackHistory" : "calls.pilot.trackCold")}
        </p>
      </section>

      {state.nextSteps.length > 0 ? (
        <section className="mt-4">
          <h4 className="crm-cp-h">{t("calls.pilot.nextSteps")}</h4>
          <ul className="mt-2 flex flex-col gap-1.5">
            {state.nextSteps.map((step) => (
              <li key={step} className="crm-cp-step">
                <span className="min-w-0 text-[12.5px] leading-[1.45]">{step}</span>
                <button
                  type="button"
                  className="crm-cp-add is-inline"
                  onClick={() => onAddToNote(step)}
                  title={t("calls.pilot.addToNote")}
                  aria-label={t("calls.pilot.addToNote")}
                >
                  <NotebookPen className="size-3" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
